import { useEffect, useMemo, useState } from "react";
import {
  fetchCompanies,
  fetchOrphanBoes,
  markOrphanBoesPaid,
  type Company,
  type OrphanBoeItem,
  type OrphanBoesDebug,
} from "../api";

/**
 * Diagnóstico y reparación de efectos "huérfanos": efectos que están
 * ABIERTOS en OBOE (SAP) pero cuyo pago ya se contabilizó desde la app.
 * Ocurre con contabilizaciones anteriores al fix que hace PATCH BoeStatus='P'
 * tras crear el JournalEntry. Aquí puedes marcarlos como Pagados en batch.
 */
export function OrphanBoes() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [items, setItems] = useState<OrphanBoeItem[] | null>(null);
  const [debug, setDebug] = useState<OrphanBoesDebug | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCompanies();
        const withSap = c.items.filter((x) => x.sap);
        setCompanies(withSap);
        if (withSap.length > 0 && !selectedCompany) setSelectedCompany(withSap[0]!.id);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!selectedCompany) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetchOrphanBoes(selectedCompany);
      setItems(r.items);
      // Por defecto marcamos solo los auto-detectados (los que casi seguro son huérfanos).
      setSelected(new Set(r.items.filter((i) => i.autoDetected).map((i) => i.boeKey)));
      setDebug(r.debug ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (boeKey: number) => {
    const next = new Set(selected);
    if (next.has(boeKey)) next.delete(boeKey);
    else next.add(boeKey);
    setSelected(next);
  };

  const toggleAll = () => {
    if (!items) return;
    setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.boeKey)));
  };

  const markPaid = async () => {
    if (!items || selected.size === 0) return;
    const n = selected.size;
    if (!confirm(`¿Marcar ${n} efecto(s) como Pagados en SAP? Esta acción hace un PATCH sobre OBOE en la empresa seleccionada.`)) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await markOrphanBoesPaid(selectedCompany, Array.from(selected));
      const okN = r.ok.length;
      const failN = r.failed.length;
      const failMsg = r.failed.length > 0
        ? "\nFallos:\n" + r.failed.map((f) => `  • BoeKey ${f.boeKey}: ${f.message}`).join("\n")
        : "";
      setResult(`✓ ${okN} marcados como Pagados (fecha ${r.paidDate}). ${failN > 0 ? `${failN} fallaron.` : ""}${failMsg}`);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const total = useMemo(() => {
    if (!items) return 0;
    return items.filter((i) => selected.has(i.boeKey)).reduce((s, i) => s + i.boeSum, 0);
  }, [items, selected]);

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Efectos huérfanos</h2>
          <p className="meta">
            Todos los efectos abiertos en OBOE (SAP) de la empresa. Los marcados con 🤖 son
            los que la app detectó (por <code>BOE NNN</code> en el memo del posting) — casi
            seguro son huérfanos. Los demás son abiertos "de verdad" en SAP; márcalos
            manualmente si sabes que ya están cerrados/pagados por otro medio.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={busy || companies.length === 0}
          >
            {companies.length === 0 && <option>Sin empresas SAP</option>}
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.alias || c.name}</option>
            ))}
          </select>
          <button type="button" onClick={load} disabled={busy || !selectedCompany}>
            {busy ? "Buscando…" : "Buscar huérfanos"}
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {result && <pre style={{ whiteSpace: "pre-wrap", background: "rgba(80,160,255,0.06)", padding: 8, borderRadius: 4 }}>{result}</pre>}

      {items !== null && items.length === 0 && (
        <div style={{ marginTop: 12 }}>
          <p className="meta">✓ No hay efectos abiertos en OBOE.</p>
          {debug && (
            <details style={{ marginTop: 8, fontSize: "0.85em" }}>
              <summary style={{ cursor: "pointer", color: "#666" }}>Diagnóstico ({debug.accountsChecked} cuentas revisadas)</summary>
              <div style={{ marginTop: 8, padding: 8, background: "rgba(120,150,200,0.06)", borderRadius: 4 }}>
                <p style={{ margin: "0 0 8px 0" }}>
                  <strong>Postings con "BOE/EFECTO NNN" detectados:</strong> {debug.postingsWithBoeCount}
                  {" · "}
                  <strong>De ésos, siguen abiertos en OBOE:</strong> {debug.openBoesMatchedByNum}
                </p>
                {debug.postingsWithBoeCount > 0 && (
                  <>
                    <p style={{ margin: "8px 0 4px 0" }}>Muestra postings BOE (los primeros 5):</p>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: "0.9em" }}>
                      {debug.postingsWithBoeSample.map((s, i) => (
                        <li key={i}><code>BoeNum {s.boeNum}</code> · {s.opDate} · {s.amount}€ — memo: "{s.memo}"</li>
                      ))}
                    </ul>
                  </>
                )}
                {debug.postingsWithBoeCount === 0 && debug.journalEntryPostingsSample.length > 0 && (
                  <>
                    <p style={{ margin: "8px 0 4px 0" }}>
                      No se detectan postings BOE con la regex. Pero hay {debug.journalEntryPostingsSample.length}+ JournalEntries contabilizados. Muestra de sus memos (¿alguno debería ser un efecto?):
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: "0.9em" }}>
                      {debug.journalEntryPostingsSample.map((s, i) => (
                        <li key={i}>{s.opDate} · {s.amount}€ — memo: "{s.memo}"</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </details>
          )}
        </div>
      )}

      {items && items.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
            <button type="button" onClick={toggleAll} disabled={busy}>
              {selected.size === items.length ? "Deseleccionar todos" : "Seleccionar todos"}
            </button>
            <span className="meta">
              {selected.size} de {items.length} seleccionados
              {" · "}
              Total: {total.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
            </span>
            <button
              type="button"
              onClick={markPaid}
              disabled={busy || selected.size === 0}
              style={{ marginLeft: "auto" }}
            >
              {busy ? "Marcando…" : `✓ Marcar ${selected.size} como Pagados en SAP`}
            </button>
          </div>

          <table className="data" style={{ fontSize: "0.9em" }}>
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th>BoeNum</th>
                <th>Tipo</th>
                <th>Estado SAP</th>
                <th>BP</th>
                <th className="num">Importe</th>
                <th>Vto.</th>
                <th>Pagado en app</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.boeKey} style={it.autoDetected ? { background: "rgba(255,200,80,0.08)" } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(it.boeKey)}
                      onChange={() => toggle(it.boeKey)}
                      disabled={busy}
                    />
                  </td>
                  <td>{it.autoDetected ? <span title="Auto-detectado por posting BOE NNN">🤖</span> : null}</td>
                  <td>{it.boeNum}</td>
                  <td>{it.boeType === "O" ? "Pagar" : "Cobrar"}</td>
                  <td><code>{it.boeStatus}</code></td>
                  <td>
                    <div style={{ fontSize: "0.9em" }}>{it.cardCode}</div>
                    <div className="meta" style={{ fontSize: "0.85em" }}>{it.cardName}</div>
                  </td>
                  <td className="num">
                    {it.boeSum.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                  </td>
                  <td>{it.dueDate}</td>
                  <td>
                    {it.postedIn ? (
                      <span>{it.postedIn.opDate} · JdtNum {it.postedIn.jdtNum}</span>
                    ) : (
                      <span className="meta">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
